import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { specialtyApi } from "../../services/specialty.api";
import { SpecialtyDto } from "../../types/specialty.definition";
import {
  createSpecialtyFailure,
  createSpecialtyRequest,
  createSpecialtySuccess,
  deleteSpecialtyFailure,
  deleteSpecialtyRequest,
  deleteSpecialtySuccess,
  fetchSpecialtiesFailure,
  fetchSpecialtiesRequest,
  fetchSpecialtiesSuccess,
  updateSpecialtyFailure,
  updateSpecialtyRequest,
  updateSpecialtySuccess,
} from "../reducers/specialtySlice";

function* fetchSpecialtiesSaga(
  action: ReturnType<typeof fetchSpecialtiesRequest>
) {
  try {
    const { lang = "fr" } = action.payload;
    const data: SpecialtyDto[] = yield call(specialtyApi.getSpecialties, lang);
    yield put(fetchSpecialtiesSuccess({ data, lang }));
  } catch (error) {
    yield put(
      fetchSpecialtiesFailure(
        error instanceof Error ? error.message : "Failed to fetch specialties"
      )
    );
  }
}

function* createSpecialtySaga(
  action: ReturnType<typeof createSpecialtyRequest>
) {
  try {
    const { data, onSuccess } = action.payload;
    yield call(specialtyApi.createSpecialties, [data]);
    yield put(createSpecialtySuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(
      createSpecialtyFailure(
        error instanceof Error ? error.message : "Failed to create specialty"
      )
    );
    const { onError } = action.payload;
    if (onError) onError();
  }
}

function* updateSpecialtySaga(
  action: ReturnType<typeof updateSpecialtyRequest>
) {
  try {
    const { id, data, onSuccess } = action.payload;
    yield call(specialtyApi.updateSpecialty, id, data);
    yield put(updateSpecialtySuccess(id));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(
      updateSpecialtyFailure(
        error instanceof Error ? error.message : "Failed to update specialty"
      )
    );
    const { onError } = action.payload;
    if (onError) onError();
  }
}

function* deleteSpecialtySaga(
  action: ReturnType<typeof deleteSpecialtyRequest>
) {
  try {
    const { id, onSuccess } = action.payload;
    yield call(specialtyApi.deleteSpecialty, id);
    yield put(deleteSpecialtySuccess(id));
    if (onSuccess) onSuccess();
  } catch (error) {
    const code: string =
      error instanceof Object &&
      "code" in error &&
      typeof (error as Record<string, unknown>).code === "string"
        ? ((error as Record<string, unknown>).code as string)
        : "UNKNOWN";
    yield put(deleteSpecialtyFailure(code));
    const { onError } = action.payload;
    if (onError) onError(code);
  }
}

export function* specialtySaga() {
  yield takeLatest(fetchSpecialtiesRequest, fetchSpecialtiesSaga);
  yield takeEvery(createSpecialtyRequest, createSpecialtySaga);
  yield takeEvery(updateSpecialtyRequest, updateSpecialtySaga);
  yield takeEvery(deleteSpecialtyRequest, deleteSpecialtySaga);
}
